import { resolveImageUrlToBlob } from '@/utils/imageUrlUtils';
import { resolveImageSpaceDisplayRawUrl } from '@/pages/ipe/workbench/utils/imageSpaceDisplayUrl';
import { convertListingImageToShein } from '@/pages/ipe/workbench/utils/convertListingImageToShein';
import { toCanonicalUploadsPath } from '@/shein-platform/modules/products/components/publishImageUploadThumbSrc';

export function resolveImageSpacePickUrl(image) {
  return resolveImageSpaceDisplayRawUrl(image) || String(image?.url || image?.src || '').trim();
}

/**
 * 图片空间选图补齐持久化地址：
 * {@code upload-pic?fromImageSpace=true} 只转平台图、不回写 OSS/图片空间，返回的 ipeUrl 为空，
 * 槽位会退化成「测网 CDN + 同页 blob」。这种空壳在模板/草稿保存时会被图片槽清洗判为不可持久化而清空，
 * 表现为「保存后再进编辑，主图/细节图都没了」。这里把图片空间记录自身的地址补回槽位。
 */
export function withImageSpaceDurableUrl(slot, image) {
  if (!slot || typeof slot !== 'object') return slot;
  if (toCanonicalUploadsPath(slot.ipeUrl)) return slot;
  const durable = toCanonicalUploadsPath(resolveImageSpacePickUrl(image));
  if (!durable) return slot;
  return {
    ...slot,
    ipeUrl: durable,
    publicUrl: toCanonicalUploadsPath(slot.publicUrl) ? slot.publicUrl : durable,
    url: toCanonicalUploadsPath(slot.url) ? slot.url : durable,
  };
}

export async function imageSpaceRecordToUploadFile(image, fallbackName = 'image.jpg') {
  const url = resolveImageSpacePickUrl(image);
  if (!url) {
    throw new Error('所选图片缺少可用地址');
  }
  const blob = await resolveImageUrlToBlob(url);
  if (!blob) {
    throw new Error('无法读取所选图片，请重试');
  }
  const rawName = String(image?.name || image?.filename || fallbackName).trim() || fallbackName;
  const fileName = rawName.includes('.') ? rawName : `${rawName}.jpg`;
  return new File(
    [blob],
    fileName,
    { type: blob.type || 'image/jpeg', lastModified: Date.now() },
  );
}

/**
 * 图片空间记录 → 发品上传槽位（走 transform-pic / upload-pic，不重复写入图片空间）。
 */
export async function convertImageSpaceToPublishSlot({
  shopId,
  image,
  imageType = 1,
  skipSheinConvert = false,
} = {}) {
  const url = resolveImageSpacePickUrl(image);
  const name = String(image?.name || image?.filename || image?.id || 'image').trim();
  if (!url) {
    throw new Error('所选图片缺少可用地址');
  }

  if (skipSheinConvert) {
    const durable = toCanonicalUploadsPath(url) || url;
    return {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name,
      previewUrl: durable,
      publicUrl: durable,
      ipeUrl: durable,
      sheinUrl: '',
    };
  }

  if (!shopId) {
    throw new Error('请先选择店铺后再选择图片');
  }

  const converted = await convertListingImageToShein({
    shopId,
    url,
    imageType,
    nameHint: name,
  });
  if (!converted?.sheinUrl) {
    throw new Error('图片转换失败，请重试');
  }

  const durable = converted.ipeUrl || converted.publicUrl || toCanonicalUploadsPath(url) || url;
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name: converted.name || name,
    previewUrl: converted.previewUrl || durable,
    publicUrl: converted.publicUrl || durable,
    ipeUrl: durable,
    sheinUrl: converted.sheinUrl,
  };
}
